/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EEtoLegalLink.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SlidingComponentLinkTemplate from "./SlidingComponentLinkTemplate";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../../redux/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import { componentStyle } from "../../../config/appTheme";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

function EEtoLegalLink({
  filterDetails,
  sortDetails,
  triggerOnSelectEE,
  selectedZoneMain,
  setSelectedZoneMain,
}) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const zoneFromurl = queryParams.get("zone");
  const eeFromurl = queryParams.get("ee_id");

  useEffect(() => {
    if (zoneFromurl) {
      setSelectedZoneMain(zoneFromurl);
    }
    if (!eeFromurl) {
      setSelectedMainEEDetails(null);
      setLinkedLegalRequirement([]);
      setEEGetTrigger((prev) => !prev);
    }
  }, [zoneFromurl, eeFromurl]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );

  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [eeCount, setEECount] = useState(0);
  const [eeComponentsMainList, setEEComponentsMainList] = useState([]);
  const [modifiedEEComponentList, setModifiedEEComponentList] = useState();
  const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
  const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
  const [zoneMainOptionsList, setZoneMainOptionsList] = useState([]);

  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [selectedMainEEDetails, setSelectedMainEEDetails] = useState(null);
  const [linkedLegalRequirement, setLinkedLegalRequirement] = useState([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [pendingEEActionDialogOpen, setPendingEEActionDialogOpen] =
    useState(false);
  const [selectedPendingEEAction, setSelectedPendingEEAction] = useState(null);
  const [eeGetTrigger, setEEGetTrigger] = useState(false);
  const [cardOpen, setCardOpen] = useState(true);
  const [disableActionButton, setDisableActionButton] = useState(false);

  useEffect(() => {
    setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
    setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
    if (
      selectedProjectDetails?.project_id &&
      selectedProjectDetails?.project_id !== projectID
    ) {
      setVariantID(null);
      setProjectID(selectedProjectDetails?.project_id);
    }
  }, [selectedProjectDetails]);

  const handleActionDialogOpen = () => {
    setActionDialogOpen(true);
  };

  const handleActionDialogClose = () => {
    setActionDialogOpen(false);
  };

  const handleActionType1DialogOpen = (ecu) => {
    console.log("---handleActionType1DialogOpen---", ecu);
    setActionType1DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const getEEActionTypeTooltip = (actionType) => {
    switch (Number(actionType)) {
      case 1:
        return "Action Pending in EE Component to EE Component linking";
      case 2:
        return "Action Pending in EE Component to Legal linking";
      case 4:
        return "Action required in EE Component Attribute";
      case 5:
        return "Action pending in EE Component Version";
      default:
        return "";
    }
  };

  const handlePendingEEActionDialogClose = () => {
    setPendingEEActionDialogOpen(false);
    setSelectedPendingEEAction(null);
  };

  const handleEEActionTypeClick = (event, item) => {
    event.stopPropagation();

    if ([4, 5].includes(Number(item?.action_type))) {
      setSelectedPendingEEAction(item);
      setPendingEEActionDialogOpen(true);
      return;
    }

    if (Number(item?.action_type) === 2) {
      handleActionDialogOpen();
    } else if (Number(item?.action_type) === 1) {
      handleActionType1DialogOpen(item);
    }
  };

  const handleRedirectionToEEComponents = () => {
    const selectedEE = selectedPendingEEAction || selectedMainEEDetails?.data;
    const selectedZoneDetails = selectedProjectZonesList?.find(
      (zone) => Number(zone?.position) === Number(selectedZoneMain),
    );
    const params = new URLSearchParams();

    if (selectedEE?.ee_id) {
      params.set("ee_id", selectedEE.ee_id);
    }
    params.set("actionMode", "eeComponentAction");
    if (selectedZoneMain) {
      params.set(
        "selectedZone",
        selectedZoneDetails?.zone_name || selectedZoneMain,
      );
      params.set("zonePosition", selectedZoneMain);
    }
    if (page) {
      params.set("page", page);
    }

    navigate(`/dashboard/v2/EESWM?${params.toString()}`);
    handlePendingEEActionDialogClose();
  };

  const confirmUpdate = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(projectID),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedMainEEDetails?.id),
      is_dependency_linking: false,
      module_type: "EE_LR",
    };
    try {
      console.log("---confirmUpdate---", reqObj);
      setDisableActionButton(true);
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: reqObj,
          show_toast: false,
        }),
      );
      console.log("---confirmUpdate response---", response);
      handleActionDialogClose();
      navigate(`/dashboard/v2/type-approval/link?zone=${selectedZoneMain}`);
    } catch (error) {
      console.error("Failed to update action type", error);
    }
    setDisableActionButton(false);
  };

  const updateChangesAndClose = () => {
    handleActionDialogClose();
    navigate(`/dashboard/v2/type-approval/link?zone=${selectedZoneMain}`);
  };

  useEffect(() => {
    setEEGetTrigger(!eeGetTrigger);
  }, []);

  useEffect(() => {
    if (selectedProjectIsZonal) {
      let filteredZoneMainOptionsList = selectedProjectZonesList
        ?.map((el) => {
          return {
            value: el?.position,
            label: el?.zone_name,
          };
        })
        ?.sort((a, b) => a.label.localeCompare(b.label));
      setZoneMainOptionsList(filteredZoneMainOptionsList);

      const zoneFromUrlValue = filteredZoneMainOptionsList?.find(
        (zone) =>
          String(zone.value) === String(zoneFromurl) ||
          String(zone.label) === String(zoneFromurl),
      );

      if (zoneFromUrlValue) {
        setSelectedZoneMain(zoneFromUrlValue?.value);
      } else if (
        selectedProjectIsZonal &&
        filteredZoneMainOptionsList?.[0]?.value &&
        !selectedZoneMain
      ) {
        setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
      } else if (selectedProjectIsZonal) {
        setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
      } else {
        setSelectedZoneMain(null);
      }
    }
  }, [selectedProjectZonesList]);

  useEffect(() => {
    console.log("selectedZoneMain123", selectedZoneMain);
  }, [selectedZoneMain]);

  useEffect(() => {
    setEEComponentsMainList([]);
    if (
      selectedSingleVariantDetails?.variant_id &&
      selectedSingleVariantDetails?.variant_id !== variantID
    ) {
      console.log(
        "---project variant change use effect---",
        selectedProjectDetails,
        selectedSingleVariantDetails,
      );
      setVariantID(selectedSingleVariantDetails?.variant_id);
    }
  }, [selectedSingleVariantDetails]);

  useEffect(() => {
    setLinkedLegalRequirement([]);
    const modifiedList = eeComponentsMainList.map((item) => {
      return {
        data: item,
        id: item.ee_id,
        cardDataArray: [
          {
            key: "Assembly Number",
            value: item.assembly_number,
            keyStyle: { fontWeight: 600 },
            valueStyle: { fontWeight: 600 },
          },
          {
            key: "Version",
            value: item.version,
          },
          {
            key: "Description",
            value: item.description,
          },
        ],
      };
    });
    setModifiedEEComponentList(modifiedList);
    if (modifiedList.length) {
      handleSelectedComponent(modifiedList[0]);
    }
  }, [eeComponentsMainList]);


  const getLegalRequiremet = async (ee_id) => {
    let apiDetails = softwareIntersectionAPI.get_ee_legal_requirements;

    let resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          ee_id: ee_id,
          project_id: selectedProjectDetails?.project_id,
          variant_id: selectedSingleVariantDetails?.variant_id,
        },
      }),
    );

    if (resp?.error) {
      setLinkedLegalRequirement([]);
    } else {
      if (!resp?.data[0]?.linked_lr_details[0].lr_id) {
        setLinkedLegalRequirement([]);
        return;
      }
      let deStructuredLinkedLr = resp?.data[0]?.linked_lr_details.map(
        (data) => {
          const ls_details = data.ls_details;
          delete data.ls_details;
          return {
            ...data,
            ...ls_details,
          };
        },
      );
      console.log("---deStructuredLinkedLr---", deStructuredLinkedLr);
      setLinkedLegalRequirement(deStructuredLinkedLr);
    }
  };

  const handleSelectedComponent = (selectedObj) => {
    setSelectedMainEEDetails(selectedObj);
    triggerOnSelectEE(selectedObj?.data?.ee_id);
    getLegalRequiremet(Number(selectedObj.id));
  };

  const unlinkLegalRequirement = async ({ lr_id, reason, callback }) => {
    let apiDetails = typeApprovalAPI.unlink_legal_from_ee;

    console.log("unlinkLegalRequirement", lr_id, reason);

    let resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ee_id: Number(selectedMainEEDetails?.id),
          removing_lr_id: lr_id,
          reason: reason,
        },
        show_toast: true,
      }),
    );

    if (resp?.error) {
      console.log("Error in unlinking");
    } else {
      getLegalRequiremet(Number(selectedMainEEDetails?.id));
      callback();
    }
  };

  const searchParams = useMemo(() => {
    if (!projectID && !variantID) {
      return null;
    }

    return {
      project_id: Number(projectID),
      variant_id: Number(variantID),
      ...(selectedProjectIsZonal && {
        zone_position: selectedZoneMain,
      }),
      ...(eeFromurl && { ee_id: Number(eeFromurl) }),
      status: "true",
      page_number: page,
      page_size: rowsPerPage,
    };
  }, [
    projectID,
    variantID,
    selectedProjectIsZonal,
    selectedZoneMain,
    selectedMainEEDetails,
    page,
    rowsPerPage,
  ]);

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          pr: 1.4,
        }}
      >
        <CustomButtonV2
          disabled={!projectID || !variantID}
          btnName="Create Ticket"
          handleOnClick={() => {
            navigate("/dashboard/v2/type-approval/link/create?tab=2");
          }}
        />
      </Box>

      <SlidingComponentLinkTemplate
        cardOpen={cardOpen}
        setCardOpen={setCardOpen}
        componentCardStyle={{
          height: "",
        }}
        contentHeight={"20rem"}
        headerTitle={
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography>Select Component</Typography>
              <Box
                onClick={() => {
                  setCardOpen(false);
                }}
                sx={{ cursor: "pointer" }}
              >
                <KeyboardArrowUpIcon />
              </Box>
            </Box>
            <Box
              display="flex"
              gap={1}
              sx={{
                width: "100%",
                mt: 0.5,
                ml: -0.5,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                {projectID && variantID && (
                  <CustomSearchbarWithApiV2
                    key={`ee-search-${projectID}-${variantID}-${selectedZoneMain}`}
                    apiAction={labCar.get_ee_components_new}
                    searchKey={"assembly_number"}
                    placeholder={"Search Assembly Number"}
                    triggerApi={eeGetTrigger}
                    getDataOnSearch={(data, resp) => {
                      if (resp.error) {
                        setEEComponentsMainList([]);
                      } else {
                        setEEComponentsMainList(resp.data);
                        setEECount(resp.total_count);
                      }
                    }}
                    params={searchParams}
                    skipApiCall={
                      !projectID ||
                      !variantID ||
                      (selectedProjectIsZonal && !selectedZoneMain)
                    }
                    iconColor={"white"}
                    txtFldStyle={componentStyle.searchField.textField}
                    skipInitialSearch={false}
                  />
                )}
              </Box>
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 40%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneMainOptionsList}
                    selectedValue={selectedZoneMain}
                    handleOnChange={(e) => setSelectedZoneMain(e.target.value)}
                    dark={true}
                    showLabel={false}
                  />
                </Box>
              )}
            </Box>
          </Box>
        }
        linkedLegalRequirement={linkedLegalRequirement}
        unlinkLegalRequirement={unlinkLegalRequirement}
        searchKeys={[
          "chapter",
          "name",
          "standard_number",
          "ls_id",
          "req_number",
        ]}
        dataList={modifiedEEComponentList}
        handleSelectedComponent={(selectedObj) => {
          handleSelectedComponent(selectedObj);
        }}
        selectedComponentDetails={selectedMainEEDetails}
        renderCardActionIcons={(item) => {
          return (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
              }}
            >
              {[1, 2, 4, 5].includes(Number(item.action_type)) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                  }}
                >
                  <Tooltip title={getEEActionTypeTooltip(item.action_type)}>
                    <IconButton
                      size="small"
                      sx={{ color: "black" }}
                      onClick={(event) => handleEEActionTypeClick(event, item)}
                    >
                      <ErrorOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        handlePageChange={(selectedPage) => {
          setPage(selectedPage);
        }}
        totalCount={eeCount}
      />
      <CustomDialogBoxV2
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
        }}
        title={"Actions"}
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        actionButtonStyle={"continuous"}
        onClickSuccess={confirmUpdate}
        onClickReject={updateChangesAndClose}
        disabled={disableActionButton}
      >
        <Box sx={{ width: "300px" }}>
          <Typography sx={{ ...componentStyle.font.text }}>
            Are you sure the intersection for ECU is completed?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={actionType1DialogOpen}
        onClose={handleActionType1DialogClose}
        title={"Actions"}
        successButtonName={"Link Now"}
        rejectButtonName={"Link Later"}
        actionButtonStyle={"continuous"}
        onClickSuccess={() => {
          console.log("---selectedMainEEDetails---", selectedMainEEDetails);

          navigate(
            `/dashboard/v2/link?project_id=${projectID}&variant_id=${Number(
              selectedSingleVariantDetails?.variant_id,
            )}&ee_id=${selectedMainEEDetails?.id
            }&selectedZone=${selectedZoneMain}&linkSoftwareModulesScreen=${true}`,
          );
        }}
        onClickReject={handleActionType1DialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography sx={{ ...componentStyle.font.text }}>
            ECU dependency linking is pending!
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={pendingEEActionDialogOpen}
        onClose={handlePendingEEActionDialogClose}
        title={"Action"}
        successButtonName={"Link Now"}
        rejectButtonName={"Link Later"}
        actionButtonStyle={"continuous"}
        onClickSuccess={handleRedirectionToEEComponents}
        onClickReject={handlePendingEEActionDialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography sx={{ ...componentStyle.font.text }}>
            {Number(selectedPendingEEAction?.action_type) === 4
              ? "Action pending in EE Component Attribute"
              : "Action pending in EE Component Version"}
          </Typography>
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

EEtoLegalLink.propTypes = {
  filterDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  sortDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  triggerOnSelectEE: PropTypes.func.isRequired,
  selectedZoneMain: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedZoneMain: PropTypes.func.isRequired,
};

export default EEtoLegalLink;
